import * as vp from 'vopay';
import React, { FunctionComponent, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLayout } from '../../../../providers/Layout.provider';
import { useBank } from '../../../../providers/Bank.provider';
import { useToken } from '../../../../providers/Token.provider';
import TopBankItem from './BankItem/TopBankItem';
import useStorage from '../../../../hooks/useStorage';
import FilteredBank from './BankItem/FilteredBank';
import useFetchFinancialInstitutions from '../../../../hooks/useApi/useFetchFinancialInstitutions';
import useFetchiQ11AccessTokenInfo from '../../../../hooks/useApi/useFetchiQ11AccessTokenInfo';
import styles from './BankConnection.module.scss';
import PageTitle from '../../../UI/PageTitle';

type BankConnectionProps = {
  setHasNoOnlineOption: (val: boolean) => void;
  setSelectedAccountService: (val: number | null) => void;
  setAccessToken: (val: string) => void;
};

enum CanadianBank {
  VOPAY = '1894',
  BMO = '1',
  SCOTIA = '2',
  RBC = '3',
  TD = '4',
  CIBC = '6',
}

const canadianBankLogos: Record<CanadianBank, string> = {
  [CanadianBank.VOPAY]: 'VoPay.svg',
  [CanadianBank.BMO]: 'BMO.svg',
  [CanadianBank.SCOTIA]: 'Scotia.svg',
  [CanadianBank.RBC]: 'RBC.svg',
  [CanadianBank.TD]: 'TD.svg',
  [CanadianBank.CIBC]: 'CIBC.svg',
};

enum ExternalBank {
  CHASE_PROD = '2',
  CHASE = '916',
  BOA = '2393',
  CITI = '10',
  BANCORP = '6',
  PNC = '21',
  CAPITAL_ONE = '17501',
  TD = '1833',
}

const externalBankLogos: Record<ExternalBank, string> = {
  [ExternalBank.CHASE_PROD]: 'Chase.svg',
  [ExternalBank.CHASE]: 'Chase.svg',
  [ExternalBank.BOA]: 'BoA.svg',
  [ExternalBank.CITI]: 'Citi.svg',
  [ExternalBank.BANCORP]: 'Bancorp.svg',
  [ExternalBank.PNC]: 'PNC.svg',
  [ExternalBank.CAPITAL_ONE]: 'CapitalOne.svg',
  [ExternalBank.TD]: 'TD.svg',
};

type FC = FunctionComponent<BankConnectionProps>;

const BankConnection: FC = (props: BankConnectionProps) => {
  const { setHasNoOnlineOption, setSelectedAccountService, setAccessToken } = props;

  const storage = useStorage();
  const { color } = useLayout();
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'BankConnection' });
  const { token } = useToken();

  const {
    FinancialInstitution,
    setFinancialInstitution,
    setBankingType,
    setAccountType,
    setSubFiniancialIntistutions,
    setMainFinancialInstitution,
  } = useBank();
  const { Box, Button, Icon, TextField, Accordion } = vp;

  const [financialInstitutionsResp, fetchFinancialInstitutions] = useFetchFinancialInstitutions({});
  const [, fetchIQ11AccessTokenInfo] = useFetchiQ11AccessTokenInfo({});
  const [searchTerm, setSearchTearm] = useState('');
  const [bankList, setBankList] = useState<vp.FinancialInstitutionInfo[]>([]);

  const [isMobile, setIsMobile] = useState(false);

  const listRef = useRef<HTMLUListElement | null>(null);
  const SandBoxBankEnabled = import.meta.env.VITE_SANDBOX_BANK_ENABLED === 'true';
  const Environment = import.meta.env.VITE_DDOG_ENV;

  const VopayBank: vp.FinancialInstitutionInfo = {
    // FinancialInstitutionID: '1894',
    FinancialInstitutionID: CanadianBank.VOPAY,
    FIName: 'VOPAY TESTING BANK',
    FINameFrench: 'VOPAY TESTING BANK',
    FinancialInstitutionNumber: '999',
    ColorCode: '',
    Country: 'CA',
    HasMoreThanOne: false,
    ExternalFIID: '0',
    AssociatedFINames: '',
    AssociatedFINamesFrench: '',
    AssociatedExternalFIIDs: '',
    VIOFinancialInstitutionsFiserv_ID: '0',
    OnlineConnectionEnabled: '1',
  };
  const defaultFinancialInstitution = {
    FinancialInstitutionID: '',
    FIName: '',
    FINameFrench: '',
    FinancialInstitutionNumber: '',
    ColorCode: '',
    Country: '',
    HasMoreThanOne: false,
    ExternalFIID: '',
    AssociatedFINames: '',
    AssociatedFINamesFrench: '',
    AssociatedExternalFIIDs: '',
    VIOFinancialInstitutionsFiserv_ID: '0',
    OnlineConnectionEnabled: '0',
  };
  const [selectedTopBankIndex, setSelectedTopBankIndex] = useState<number | null>(null);
  const [startX, setStartX] = useState<number | null>(300);
  const [bankListPosition, setBankListPosition] = useState<number>(0);
  const [isBankListVisible, setIsBankListVisible] = useState(false);
  const [isBankNotFound, setIsBankNotFound] = useState(false);
  const [hasSearchResults, setHasSearhResults] = useState(false);
  const [topFinancialInstitutions, setTopFinancialInstitutions] = useState<vp.FinancialInstitutionInfo[]>([]);
  const [isTopFinancialInstitutionsSet, setIsTopFinancialInstitutionsSet] = useState(false);
  const [selectedFinancialInstitution, setSelectedFinancialInstitution] = useState(defaultFinancialInstitution);
  const [selectedBankName, setSelectedBankName] = useState('');
  const [selectedFIID, setSelectedFIID] = useState('');
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);

  useEffect(() => {
    setIsMobile(window.innerWidth <= 480);
  }, [window.innerWidth]);

  window.addEventListener('resize', () => {
    setIsMobile(window.innerWidth <= 480);
  });

  useEffect(() => {
    fetchFinancialInstitutions({
      Country: token?.Country,
      Search: searchTerm,
      Version: 'v3',
    });
  }, []);

  useEffect(() => {
    if (token?.Country === 'US') {
      const IframeKey = storage.token.getIQ11Token().iQ11Token;
      const PaymentSelectionMethod = 'bank';

      const processAccessToken = async () => {
        const ForOnboarding = storage.token.getIQ11Token().iQ11ForOnboarding ?? false;
        const accessTokenInfo = await fetchIQ11AccessTokenInfo({ IframeKey, PaymentSelectionMethod, ForOnboarding });

        if (accessTokenInfo.Success) {
          setAccessToken(accessTokenInfo.AccessToken);
        }
      };

      processAccessToken().catch((error) => console.error(error));
    }
  }, [token]);

  // filter financial institutions
  useEffect(() => {
    const FilteredBankList = Object.values(financialInstitutionsResp.FinancialInstitutions || {}).map((Institution) => {
      // TODO: figure out enum mapping
      const HasMoreThanOne =
        Institution.FinancialInstitutionID === '1833' || Institution.FinancialInstitutionID === '1692';
      if (Institution.FinancialInstitutionID === '1833') {
        return {
          ...Institution,
          HasMoreThanOne,
        } as vp.FinancialInstitutionInfo;
      }
      return { ...Institution, HasMoreThanOne } as vp.FinancialInstitutionInfo;
    });
    // if condition for poduction
    if (SandBoxBankEnabled) {
      if (searchTerm.length <= 2 || VopayBank.FIName.toLowerCase().includes(searchTerm.toLowerCase())) {
        FilteredBankList.unshift(VopayBank);
      }
    }
    setBankList(FilteredBankList);
  }, [financialInstitutionsResp]);

  // set top financial institutions
  useEffect(() => {
    if (!isTopFinancialInstitutionsSet && Object.values(bankList).length > 1) {
      const topCABankIDs: string[] = SandBoxBankEnabled
        ? [CanadianBank.VOPAY, CanadianBank.BMO, CanadianBank.SCOTIA, CanadianBank.RBC, CanadianBank.TD]
        : [CanadianBank.BMO, CanadianBank.SCOTIA, CanadianBank.RBC, CanadianBank.TD, CanadianBank.CIBC];
      const topUSBankIDs: string[] =
        Environment === 'LocalDev'
          ? [
              ExternalBank.CHASE,
              ExternalBank.BOA,
              ExternalBank.CITI,
              ExternalBank.BANCORP,
              ExternalBank.PNC,
              ExternalBank.CAPITAL_ONE,
              ExternalBank.TD,
            ]
          : [
              ExternalBank.CHASE_PROD,
              ExternalBank.BOA,
              ExternalBank.CITI,
              ExternalBank.BANCORP,
              ExternalBank.PNC,
              ExternalBank.CAPITAL_ONE,
              ExternalBank.TD,
            ];

      const filteredUSBanks = Object.values(bankList).filter((bank) => topUSBankIDs.includes(bank.ExternalFIID));
      const uniqueUSBanks = filteredUSBanks.reduce<vp.FinancialInstitutionInfo[]>((acc, bank) => {
        if (!acc.some((existingBank) => existingBank.ExternalFIID === bank.ExternalFIID)) {
          acc.push(bank);
        }
        return acc;
      }, []);

      const topBanks =
        token?.Country === 'CA'
          ? Object.values(bankList || {})
              .filter((bank) => topCABankIDs.includes(String(bank.FinancialInstitutionID)))
              .sort((a, b) => (a.FinancialInstitutionID < b.FinancialInstitutionID ? -1 : 1))
          : uniqueUSBanks;
      setTopFinancialInstitutions(topBanks);
      setIsTopFinancialInstitutionsSet(true);
    }
  }, [bankList]);

  // search financial institutions
  useEffect(() => {
    const delaySearchInstitutions = setTimeout(() => {
      if (searchTerm.length >= 2) {
        fetchFinancialInstitutions({
          Country: token?.Country,
          Search: searchTerm,
          Version: 'v3',
        });
        setHasSearhResults(true);
      }
    }, 500);

    return () => clearTimeout(delaySearchInstitutions);
  }, [searchTerm]);

  useEffect(() => {
    if (hasSearchResults) {
      if (bankList.length >= 1 && selectedFinancialInstitution.FIName === '' && searchTerm !== '') {
        setIsBankListVisible(true);
        setHasSearhResults(false);
      } else if (bankList.length === 0 && searchTerm !== '') {
        setIsBankNotFound(true);
      }
    } else {
      setIsBankListVisible(false);
      setIsBankNotFound(false);
    }
  }, [bankList, searchTerm]);

  // set selected top Bank
  useEffect(() => {
    if (selectedFinancialInstitution.FIName === '') {
      setSelectedTopBankIndex(null);
    }
  }, [selectedFinancialInstitution]);

  useEffect(() => {
    const selectedTopBankIdx = Object.values(topFinancialInstitutions || {}).findIndex(
      (bank) => bank.FinancialInstitutionID === selectedFinancialInstitution.FinancialInstitutionID,
    );

    if (selectedTopBankIdx !== -1) {
      setSelectedTopBankIndex(selectedTopBankIdx);
    }
  }, [selectedFinancialInstitution]);

  // set bank connection type option
  useEffect(() => {
    if (
      (selectedFinancialInstitution.FIName !== '' && selectedFinancialInstitution.OnlineConnectionEnabled === '0') ||
      selectedFinancialInstitution.FIName === 'Not Found'
    ) {
      setHasNoOnlineOption(true);
    } else {
      setHasNoOnlineOption(false);
    }

    if (selectedFinancialInstitution.FIName === '') {
      setBankingType(undefined);
      setAccountType(undefined);
    }
  }, [selectedFinancialInstitution]);

  useEffect(() => {
    storage.paymentMethod.storePaymentMethod('bank');
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (listRef.current) {
        setBankListPosition(listRef.current.scrollLeft);
      }
    };

    const currentListRef = listRef.current;
    if (currentListRef) {
      currentListRef.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (currentListRef) {
        currentListRef.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTearm(event.target.value);
  };

  const SelectedBankItem = (financialInstitution: vp.FinancialInstitutionInfo) => {
    const AssociatedFINames = financialInstitution.AssociatedFINames?.split(',');
    const AssociatedFINamesFrench = financialInstitution.AssociatedFINamesFrench?.split(',');
    const AssociatedExternalFIIDs = financialInstitution.AssociatedExternalFIIDs?.split(',');
    setSelectedAccountService(null);
    setFinancialInstitution(financialInstitution);
    setSelectedFinancialInstitution(financialInstitution);
    setSearchTearm('');
    setSelectedBankName(financialInstitution.FIName);
    setSelectedFIID(financialInstitution.ExternalFIID);

    if (AssociatedFINames?.length > 1) {
      setMainFinancialInstitution(financialInstitution);
      const SubBanks: vp.FinancialInstitutionInfo[] = AssociatedFINames.map((data, index) => ({
        AssociatedFINames: financialInstitution.AssociatedFINames,
        AssociatedFINamesFrench: financialInstitution.AssociatedFINamesFrench || '',
        ColorCode: financialInstitution.ColorCode,
        FinancialInstitutionNumber: financialInstitution.FinancialInstitutionNumber,
        FIName: data,
        FINameFrench: AssociatedFINamesFrench && AssociatedFINamesFrench[index],
        ExternalFIID: AssociatedExternalFIIDs[index] || '',
        AssociatedExternalFIIDs: AssociatedExternalFIIDs[index],
        FinancialInstitutionID: financialInstitution.FinancialInstitutionID,
        VIOFinancialInstitutionsFiserv_ID: financialInstitution.VIOFinancialInstitutionsFiserv_ID,
        HasMoreThanOne: true,
        Country: financialInstitution.Country,
        OnlineConnectionEnabled: financialInstitution.OnlineConnectionEnabled,
      }));
      if (token?.Country === 'CA') {
        setSubFiniancialIntistutions(SubBanks);
      } else {
        setSubFiniancialIntistutions([]);
      }
    } else {
      setSubFiniancialIntistutions([]);
    }
    if (financialInstitution.OnlineConnectionEnabled === '0') {
      setBankingType('manual');
    } else if (token?.AccountSelectionMethod === 'online') {
      setBankingType('online');
    } else if (token?.AccountSelectionMethod === 'manual') {
      setBankingType('manual');
    }
  };

  const ConnectManually = () => {
    setFinancialInstitution({
      AssociatedFINames: '',
      AssociatedFINamesFrench: '',
      AssociatedExternalFIIDs: '',
      ColorCode: '',
      Country: '',
      FIName: 'Not Found',
      FINameFrench: '',
      FinancialInstitutionID: '',
      FinancialInstitutionNumber: '',
      ExternalFIID: '',
      VIOFinancialInstitutionsFiserv_ID: '',
      HasMoreThanOne: false,
      OnlineConnectionEnabled: '0',
    });
    setMainFinancialInstitution({
      AssociatedFINames: '',
      AssociatedFINamesFrench: '',
      AssociatedExternalFIIDs: '',
      ColorCode: '',
      Country: '',
      FIName: '',
      FINameFrench: '',
      FinancialInstitutionID: '',
      FinancialInstitutionNumber: '',
      ExternalFIID: '',
      VIOFinancialInstitutionsFiserv_ID: '',
      HasMoreThanOne: false,
      OnlineConnectionEnabled: '0',
    });
    setSelectedFinancialInstitution({
      ...defaultFinancialInstitution,
      FIName: 'Not Found',
    });

    setBankingType('manual');
    setSelectedAccountService(null);
    setSubFiniancialIntistutions([]);
    setIsBankListVisible(false);
    setIsBankNotFound(false);
  };

  // bank list slider
  const handleStart = (clientX: number) => {
    setStartX(clientX);
  };

  const handleMove = (clientX: number) => {
    if (startX === null || !listRef.current) return;

    const deltaX = startX - clientX;

    if (deltaX !== 0) {
      document.body.style.overflow = 'hidden';
      listRef.current.scrollLeft += deltaX;
    }
  };

  const handleEnd = () => {
    setStartX(null);
    document.body.style.overflow = 'auto';
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  const handleLeft = () => {
    if (!listRef.current) return;
    listRef.current.scrollLeft -= 300;
    setBankListPosition(bankListPosition - 300);
  };

  const handleRight = () => {
    if (!listRef.current) return;
    listRef.current.scrollLeft += 300;
    setBankListPosition(bankListPosition + 300);
  };

  const GetFilteredBanks = bankList
    .filter((bank) => bank !== undefined)
    .map((bank) => (
      <FilteredBank
        key={bank.FinancialInstitutionID}
        onClick={() => {
          SelectedBankItem(bank);
        }}
        FinancialInstitution={bank}
      />
    ));

  const selectBank = () => (
    <>
      {FinancialInstitution?.FIName !== '' &&
      FinancialInstitution?.FIName !== 'Not Found' &&
      FinancialInstitution?.FIName !== undefined &&
      !isAccordionOpen ? (
        <div className={styles.subBodyContainer}>
          {token?.Country === 'CA' ? (
            <>
              {canadianBankLogos[FinancialInstitution?.FinancialInstitutionID as CanadianBank] && (
                <img
                  src={`/images/banks-circular-logos/CA/${
                    canadianBankLogos[FinancialInstitution?.FinancialInstitutionID as CanadianBank]
                  }`}
                  alt={FinancialInstitution?.FIName}
                />
              )}
            </>
          ) : (
            <>
              {externalBankLogos[FinancialInstitution?.ExternalFIID as ExternalBank] && (
                <img
                  src={`/images/banks-circular-logos/US/${
                    externalBankLogos[FinancialInstitution?.ExternalFIID as ExternalBank]
                  }`}
                  alt={FinancialInstitution?.FIName}
                />
              )}
            </>
          )}

          <div className={styles.bankInformation}>
            {token?.Country === 'CA' && (
              <>
                <span>{FinancialInstitution?.FinancialInstitutionNumber}</span>
                <span>|</span>
              </>
            )}
            <span>
              {selectedBankName}
              {token?.Country === 'CA' && ` (${selectedFIID})`}
            </span>
          </div>
        </div>
      ) : null}
    </>
  );

  return (
    <Box width="100%" height="auto" flx="col" jc="center" ai="center">
      <Box width="100%" flx="col" ai="center">
        <Box gap="common" flx="col">
          <Box ai="center" jc="center" width="100%" flx="col">
            <PageTitle title={<>{t('connectBankTitle')}</>} textSize="40px" bold />

            <div className={styles.descriptionContainer}>{t('connectBankDescription')}</div>
          </Box>
          <Accordion
            title={`1. ${t('selectBankTitle')}`}
            iconColor={token?.DarkMode ? 'primary-light' : 'primary-dark'}
            iconSize="xsm"
            variant="box"
            bodyPadding="0px"
            titlePadding="0px"
            open={isAccordionOpen}
            onClick={() => {
              setIsAccordionOpen(!isAccordionOpen);
            }}
            subBody={selectBank()}
            body={
              <Box flx="col">
                <div className={styles.bankListsWrapper}>
                  <div
                    className={[styles.bankListsContainer, styles[getCountryClass()]].join(' ')}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    <div
                      style={
                        isMobile && bankListPosition === 0 ? { display: 'none' } : !isMobile ? { display: 'none' } : {}
                      }
                      className={[styles.slideBtn, styles.leftSlideBtn].join(' ')}
                    >
                      <Button
                        id="slide-left-btn"
                        label=""
                        background="#FFFFFF"
                        icon="arrowLeft"
                        color="primary"
                        iconLocation="center"
                        iconSize="xlg"
                        width="40px"
                        height="40px"
                        iconWidth="18px"
                        iconHeight="18px"
                        isCircle
                        onClick={handleLeft}
                      />
                    </div>
                    <div
                      style={
                        isMobile && bankListPosition >= 100 ? { display: 'none' } : !isMobile ? { display: 'none' } : {}
                      }
                      className={[styles.slideBtn, styles.rightSlideBtn].join(' ')}
                    >
                      <Button
                        id="slide-right-btn"
                        label=""
                        background="#FFFFFF"
                        icon="arrowRight"
                        color="primary"
                        iconLocation="center"
                        iconSize="xlg"
                        width="40px"
                        height="40px"
                        iconWidth="18px"
                        iconHeight="18px"
                        isCircle
                        onClick={handleRight}
                      />
                    </div>
                    <ul ref={listRef}>
                      {topFinancialInstitutions
                        ?.sort((a, b) => {
                          if (
                            String(a.FinancialInstitutionID) === '1894' ||
                            String(a.FinancialInstitutionID) === '1898'
                          )
                            return -1;
                          if (
                            String(b.FinancialInstitutionID) === '1894' ||
                            String(a.FinancialInstitutionID) === '1898'
                          )
                            return 1;
                          return 0;
                        })
                        .map((bank, index) => (
                          <div key={bank.FinancialInstitutionID} className={styles[getSelectedBankClass(index)]}>
                            <div className={styles.border} style={{ borderColor: color }}>
                              Connect
                            </div>
                            <TopBankItem
                              bank={bank}
                              selectedClass={selectedTopBankIndex === index ? 'selected' : ''}
                              onClick={() => {
                                setSelectedTopBankIndex(index);
                                SelectedBankItem(bank);
                              }}
                            />
                          </div>
                        ))}
                    </ul>
                  </div>
                </div>
                <div className={styles.optionContainer}>
                  <span>{t('or')}</span>
                </div>
                <Box flx="col" position="relative">
                  <button
                    className={styles.searchFormContainer}
                    type="button"
                    onClick={() => {
                      setSelectedFinancialInstitution(defaultFinancialInstitution);
                      setFinancialInstitution({
                        AssociatedFINames: '',
                        AssociatedFINamesFrench: '',
                        AssociatedExternalFIIDs: '',
                        ColorCode: '',
                        Country: '',
                        FIName: 'Not Found',
                        FINameFrench: '',
                        FinancialInstitutionID: '',
                        FinancialInstitutionNumber: '',
                        ExternalFIID: '',
                        VIOFinancialInstitutionsFiserv_ID: '',
                        HasMoreThanOne: false,
                        OnlineConnectionEnabled: '0',
                      });
                    }}
                  >
                    <Icon
                      icon="search2"
                      color={token?.DarkMode ? 'primary-light' : 'primary-dark'}
                      width="12px"
                      height="12px"
                    />
                    {selectedFinancialInstitution.FIName !== '' ? (
                      selectedFinancialInstitution.FIName !== 'Not Found' ? (
                        <div className={styles.selectedBankContainer}>
                          {token?.Country === 'CA' && (
                            <span className={styles.number}>
                              {selectedFinancialInstitution?.FinancialInstitutionNumber ||
                                selectedFinancialInstitution?.FinancialInstitutionID}{' '}
                              |{' '}
                            </span>
                          )}
                          <span className={styles.title}>
                            {i18n.language === 'en'
                              ? selectedFinancialInstitution?.FIName
                              : selectedFinancialInstitution?.FINameFrench}
                          </span>
                          {token?.Country === 'CA' && <span> ({selectedFinancialInstitution?.ExternalFIID})</span>}
                        </div>
                      ) : (
                        <div className={styles.notfoundContainer}>
                          <span>{t('notFoundTitle')} | </span>
                          {t('connectManuallyButton')}
                        </div>
                      )
                    ) : (
                      <div className={styles.searchInputContainer}>
                        <TextField
                          name="BankInstitution"
                          variant="none"
                          preset="text"
                          width="100%"
                          height="26px"
                          onChange={handleChange}
                          placeholder={`${t('searchFiniancialInstitutionPlaceHolder')}`}
                          onBlur={() => {}}
                        />
                      </div>
                    )}
                  </button>
                  {(isBankListVisible || isBankNotFound) && (
                    <div className={styles.bankSelectItemsContainer} data-testid="fi-dropdown">
                      {isBankListVisible ? (
                        GetFilteredBanks
                      ) : (
                        <Box flx="col" gap="sm">
                          <div className={styles.noResultsTitle}>{t('noResultsTitle')}</div>
                          <div className={styles.noResultsContent}>
                            <b>{t('bankNotFoundTitle')}</b>
                            <Button
                              id="connect-manually-button"
                              label={t('connectManuallyButton') || ''}
                              color="none"
                              type="button"
                              background="transparent"
                              textColor="#2D2D2D"
                              width={i18n.language === 'en' ? '140px' : '190px'}
                              noShadow
                              noPadding
                              fontWeight={400}
                              textSize={15}
                              fontUnit="px"
                              onClick={ConnectManually}
                            />
                          </div>
                        </Box>
                      )}
                    </div>
                  )}
                </Box>
              </Box>
            }
          />
        </Box>
      </Box>
    </Box>
  );

  function getSelectedBankClass(index: number) {
    return selectedFinancialInstitution.FIName !== '' && selectedTopBankIndex === index ? 'selected' : '';
  }

  function getCountryClass() {
    return token?.Country === 'US' ? 'usListsContainer' : '';
  }
};

export default BankConnection;
